export interface NavEntry {
    key: string;
    label: string;
    to: string;
    glyph: string;
    indented: boolean;
    /**
     * Marks entries that will require the `daIntelligence` entitlement. Today every `pro: true`
     * entry is badged unconditionally for everyone — the entitlement check that would badge only
     * those without it lands in a later plan.
     */
    pro: boolean;
}

/**
 * The prototype's navigation, as data.
 *
 * Pure and exported separately from the component so both real configurations can be asserted
 * without rendering: seven entries with the marketplace flags on, four with them off. The reduced
 * one is not hypothetical — it is exactly what production shows the day the redesign gate flips
 * while the marketplace stays dark.
 */
export function navigationFor(flags: { marketplace: boolean; deals: boolean }): NavEntry[] {
    const entries: NavEntry[] = [
        // /dashboard, not "/" — the root is the PUBLIC marketplace now, so a signed-in customer
        // clicking Dashboard here would be thrown out of the app onto the sales page.
        { key: 'dashboard', label: 'Dashboard', to: '/dashboard', glyph: '◈', indented: false, pro: false },
        { key: 'search', label: 'Search', to: '/search', glyph: '⌕', indented: true, pro: true },
        { key: 'saved', label: 'Saved', to: '/saved', glyph: '☆', indented: true, pro: true },
    ];

    if (flags.deals) {
        // Directly under Saved, in the same indented group: saved DAs are what a deal is worked
        // up from. `deals` is the internal identifier — the flag, the route's page and this key
        // all still say deals. Only the LABEL was renamed to Sales Pipeline.
        entries.push({
            key: 'deals', label: 'Sales Pipeline', to: '/opportunities/board', glyph: '◫', indented: true, pro: false,
        });
    }

    entries.push({ key: 'alerts', label: 'Alert settings', to: '/alert-settings', glyph: '✉', indented: false, pro: true });

    if (flags.marketplace) {
        entries.push(
            { key: 'listing', label: 'Marketplace', to: '/my-listing', glyph: '◧', indented: false, pro: false },
            { key: 'enquiries', label: 'Enquiries', to: '/enquiries', glyph: '⊡', indented: true, pro: false },
            // The public Send my Business page — a marketplace product, so it sits under Marketplace.
            { key: 'send-my-business', label: 'Send my Business', to: '/send-my-business', glyph: '✉', indented: true, pro: false },
        );
    }

    return entries;
}

export interface TabEntry {
    key: string;
    label: string;
    /** 'more' has no route — it is a button that opens the More sheet, not a link. */
    to: string;
    badge?: 'enquiries' | 'saved';
    /**
     * The name a screen reader announces when `label` is a shortened form. Five tabs share a
     * phone's width equally, so a two-word label wraps onto a second line and knocks its tab out
     * of line with the rest; the bar shows the short form, the sidebar and assistive tech the full.
     */
    fullLabel?: string;
}

/**
 * The prototype's mobile tab bar, as data. Same pure-function-plus-component split as
 * {@link navigationFor}, for the same reason: both real configurations need to be asserted
 * without rendering.
 *
 * The bar always holds exactly five items. When the marketplace is dark there is no Enquiries
 * inbox and no Sales Pipeline board to put in the last two slots, so Saved and Alert settings — which would
 * otherwise live only in the More sheet — are promoted into the vacated slots instead. Whatever the
 * bar does not take, the sheet holds. This keeps the bar's shape constant across both
 * configurations rather than ever rendering with empty slots.
 */
export function tabsFor(flags: { marketplace: boolean; deals: boolean }): TabEntry[] {
    const tabs: TabEntry[] = [
        // See navigationFor: "/" is the public marketplace now.
        { key: 'dashboard', label: 'Dashboard', to: '/dashboard' },
        { key: 'map', label: 'Search', to: '/search' },
    ];

    if (flags.marketplace) {
        tabs.push({ key: 'enquiries', label: 'Enquiries', to: '/enquiries', badge: 'enquiries' });
    } else {
        tabs.push({ key: 'saved', label: 'Saved', to: '/saved', badge: 'saved' });
    }

    if (flags.deals) {
        tabs.push({ key: 'deals', label: 'Pipeline', fullLabel: 'Sales Pipeline', to: '/opportunities/board' });
    } else {
        tabs.push({ key: 'alerts', label: 'Alerts', fullLabel: 'Alert settings', to: '/alert-settings' });
    }

    tabs.push({ key: 'more', label: 'More', to: 'more' });

    return tabs;
}
